import React, { useState, useEffect } from "react";
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

import { withRouter } from 'react-router-dom';
import withShowModal from "../../../../hocs/with_show_modal.js";
import withShowToast from '../../../../hocs/with_show_toast.js';
import withCurrentUser from '../../../../hocs/with_current_user.js';
import { compose } from 'redux';

import I18n from "../../../../base/i18n.js.jsx";
import MoneyFormatter from "../../../../utils/money_formatter.js";

import RevenueAndPeriodBlock from "../../../../base/project_investments/revenue_and_period_block.js";
import ProjectsInvestmentsInfo from "../../../../base/project_investments/projects_investments_info.js";
import BonusRatesBlock from "../../../../base/project_investments/bonus_rates_block.js.jsx";

import ParticipantInfo from "../../../../base/user_investments/participant_info.js";

import SubscribeBlock from "./subscribe_block.js";
import ButtonLink from "../../../../base/links/button_link.js";
import ProjectStatusEnum from "../../../../enums/project_status.js";

import NewslettersSubscriptionUseCase from "../../../../use_cases/newsletters/subscription.js.jsx";
import CreateTokenUseCase from "../../../../use_cases/recaptcha/create_token_use_case.js.jsx";

import { ProjectModel } from "../../../../models/projects/project_model.js";

import Routes from "../../../routes.js";

const subscribeAction = "project_subscribe";

const blockStyles = makeStyles(theme => ({
  root: {
    borderRadius: "4px",
    boxShadow: "0px 4px 7px rgba(0, 0, 0, 0.18)"
  },
  header: {
    borderRadius: "4px 4px 0px 0px",
    padding: "24px 30px",
    fontSize: "28px",
    lineHeight: "34px",
    color: theme.palette.white,
    backgroundColor: theme.palette.blueMain,
    textAlign: "center",
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: "uppercase",
    ['@media (max-width: 768px)']: {
      padding: "12px 16px",
      fontSize: "16px",
      lineHeight: "20px",
    },
  },
  revenueAndPeriod: {
    ['@media (max-width: 768px)']: {
      padding: "12px 0px",
    },
  },
  rows: {

  },
  row: {
    padding: "20px 32px",
    display: "flex",
    fontSize: "16px",
    alignItems: "center",
    "& + &": {
      borderTop: `1px solid ${theme.palette.grayDivider}`
    },
    "&:last-child": {
      borderBottom: `1px solid ${theme.palette.grayDivider}`
    },
    ['@media (max-width: 768px)']: {
      fontSize: "13px",
      padding: "14px 18px",
    },
  },
  labelColumn: {
    flex: "0 1 40%",
    textAlign: "left",
    paddingRight: "5px"
  },
  longText: {
    display: "block",
    ['@media (max-width: 768px)']: {
      display: "none"
    },
  },
  shortText: {
    display: "none",
    ['@media (max-width: 768px)']: {
      display: "block"
    },
  },
  valueColumn: {
    flex: "0 1 60%",
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: "right",
    paddingLeft: "5px"
  },
  largeLabel: {
    flex: "0 1 40%",
    fontSize: "18px",
    fontWeight: 500,
    textAlign: "left",
    paddingRight: "5px",
    textTransform: "capitalize",
    ['@media (max-width: 768px)']: {
      fontSize: "12px"
    }
  },
  largeValue: {
    flex: "0 1 60%",
    fontSize: "28px",
    fontWeight: 500,
    textAlign: "right",
    paddingLeft: "5px",
    ['@media (max-width: 768px)']: {
      fontSize: "16px"
    }
  },
  bottomRow: {
    padding: "16px 32px 32px",
    ['@media (max-width: 768px)']: {
      padding: "16px 18px 26px",
    },
  },
  button: {
    width: "100%",
    fontSize: "20px",
    padding: "12px 20px",
    ['@media (max-width: 768px)']: {
      fontSize: "18px",
      padding: "8px 20px",
    },
    "& > span": {
      color: theme.palette.white
    },
    "&:hover": {
      textDecoration: "none !important",
    }
  },
  participantInfo: {
    fontWeight: theme.typography.fontWeightMedium,
    margin: "0px -32px 30px",
    ['@media (max-width: 768px)']: {
      margin: "5px -18px 20px",
    },
  },
  participantInfoInvestmentStatus: {
    ['@media (max-width: 768px)']: {
      padding: "0px 5px"
    },
  },
  participantInfoInfoRow: {
    padding: "0px 32px",
    marginTop: "25px",
    ['@media (max-width: 768px)']: {
      padding: "0px 16px",
      marginTop: "16px",
    },
  },
  participantInfoDate: {
    fontSize: "14px",
    ['@media (max-width: 768px)']: {
      fontSize: "10px",
      marginBottom: "7px"
    },
  },
  participantInfoAmountRow: {
    fontSize: "18px",
    ['@media (max-width: 768px)']: {
      fontSize: "14px",
    },
  },
  participantInfoMultipleRow: {
    padding: "0px 32px",
    ['@media (max-width: 768px)']: {
      padding: "0px 16px",
    },
  }
}));

const InvestFormBlock = (props) => {

  const [formLoaded, setFormLoaded] = useState(false);
  const [participant, setParticipant] = useState(props.participant || {});
  const [submitting, setSubmitting] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState(props.currentUser ? props.currentUser.email : "");

  const classes = blockStyles();
  const { className, project, allowToInvest, allowToSubscribe } = props;
  const { history, showSuccessMessage, showErrorMessage, showWarningMessage } = props;
  const { investments = [] } = participant || {};

  useEffect(() => {
    setParticipant(props.participant)
  }, [props.participant])

  useEffect(() => {
    setFormLoaded(props.userOnceLoaded && props.formLoaded)
  }, [props.userOnceLoaded, props.formLoaded])

  const onSubmit = (event) => {
    event.preventDefault();
    const { id : projectId, slug: projectSlug } = project;
    const { currentUser } = props;

    if(allowToSubscribe && !participant) {
      setSubmitting(true)

      const params = {
        email: subscribeEmail
      }

      grecaptcha.ready(() => {
        CreateTokenUseCase.execute(subscribeAction)
          .then(token => {
            return NewslettersSubscriptionUseCase.execute(params, token)
          })
          .then(() => {
            setSubmitting(false);
            showSuccessMessage(I18n.t("web.subscription.success"));
          })
          .catch(result => {
            const { message } = result;
            setSubmitting(false);
            showErrorMessage(message);
          })
      })
    }
    else {
      if(!currentUser) {
        showWarningMessage(I18n.t("errors.unauthorized"));
        history.push({
          pathname: Routes.signUp(),
          state: {
            projectId: projectId,
            projectSlug: projectSlug
          }
        });
        return;
      }
    }
  }

  const onChangeEmail = (value) => {
    setSubscribeEmail(value);
  }

  return (
    <div className = { clsx(classes.root, className) } >
      <div className = { classes.header } >
        { ProjectStatusEnum.label(project.status) }
      </div>
      <RevenueAndPeriodBlock project = { project }
                             className = { classes.revenueAndPeriod } />
      <BonusRatesBlock project = { project } className = { classes.revenueAndPeriod } />
      <form onSubmit = { onSubmit } >
        <div className = { classes.rows } >
          <div className = { classes.row } >
            <div className = { classes.largeLabel } >
              { ProjectModel.fieldDisplayName("investForm") }
            </div>
            <div className = { classes.largeValue } >
              { project.ratingMark !== null ?  project.ratingMark : I18n.t("web.page.project.invest_form.load_request_purchase") }
            </div>
          </div>
          {
            project.minInvestmentAmount &&
            <div className = { classes.row } >
              <div className = { classes.largeLabel } >
                {  ProjectModel.fieldDisplayName("minInvestmentAmount") }
              </div>
              <div className = { classes.largeValue } >
                { MoneyFormatter.format(project.minInvestmentAmount) }
                &nbsp;
                { I18n.t("currency.euro") }
              </div>
            </div>
          }
        </div>
        <ProjectsInvestmentsInfo project = { project } />
        <div className = { classes.bottomRow } >
          { allowToSubscribe && !participant &&
            <SubscribeBlock onChangeEmail = { onChangeEmail }
                            subscribeEmail = { subscribeEmail }
                            submitting = { submitting } />
          }
          <ParticipantInfo participant = { participant }
                           project = { project }
                           className = { classes.participantInfo }
                           classes = { {
                            investmentStatus: classes.participantInfoInvestmentStatus,
                            info: classes.participantInfoInfoRow,
                            date: classes.participantInfoDate,
                            amountRow: classes.participantInfoAmountRow,
                            multipleRow: classes.participantInfoMultipleRow
                           } } />
          {
            allowToInvest &&
            <ButtonLink className = { classes.button }
                        disabled = { !formLoaded }
                        to = { Routes.invest(project) } >
              { investments.length > 0 ? I18n.t("action.invest_now_again") : I18n.t("action.invest_now") }
            </ButtonLink>
          }
        </div>
      </form>
    </div>
  );
}

export default compose(withRouter, withShowModal, withShowToast, withCurrentUser)(InvestFormBlock);
